<template>
  <div>
    <div class="bg-blue-900 p-4 lg:p-12">
      <div class="max-w-5xl mx-auto mb-12">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between">
          <h4 class="text-white font-bold">Clients</h4>

          <BaseButton type="button" info class="ml-1" :disabled="exporting" @click="handleExport">
            <Loader v-if="exporting" inverse small />
            <span v-else>Export</span>
          </BaseButton>
        </div>
      </div>
    </div>
    <div class="px-4 pb-4 lg:px-12 lg:pb-12 -mt-12">
      <div class="max-w-5xl mx-auto">
        <div class="space-y-16">
          <VList :meta="meta">
            <template #before>
              <div class="hidden lg:flex font-medium text-sm text-gray-600 mb-4 pr-2">
                <div class="flex-1 px-4">Name</div>
                <div class="w-32 px-4 text-right">Balance</div>
                <div class="w-32 px-4 text-right">Credit Limit</div>
                <div class="w-32 px-4 text-right">Remaining Credit Limit</div>
                <div class="w-48 px-4 mr-12 text-right">Date Created</div>
              </div>
            </template>

            <template v-if="isLoading" #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                <Loader />
              </div>
            </template>

            <template v-else-if="isRejected" #default>
              <div
                class="odd:bg-red-100 border border-red-200 rounded p-4 text-red-600 text-center"
              >
                Something went wrong. Please try again later.
              </div>
            </template>

            <template v-else-if="clients.length" #default>
              <template v-for="client in clients">
                <VListLine
                  :key="`clients_${client.uuid}`"
                  :client="client"
                  :disabled="isLoading"
                  @client:view="viewClient"
                />
              </template>
            </template>

            <template v-else #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                No results found
              </div>
            </template>
          </VList>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { snakeCase } from 'lodash-es';
import { middleware } from '@/routes/guards';
import store from '@/store';
import { accountAccountClientsClient } from '@/api/clients';

import VList from '@/components/Common/VList.vue';
import VListLine from '@/components/AccountClients/VAccountListLine.vue';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

export default {
  components: {
    VList,
    VListLine,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    status: STATUS_IDLE,
    exporting: false,
  }),
  computed: {
    ...mapState('ui/accountClients', ['meta']),
    ...mapGetters('ui/accountClients', ['clients']),
    account() {
      return this.$store.state.entities.accounts.byId[this.accountId];
    },
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
  },
  watch: {
    $route: 'fetchData',
  },
  destroyed() {
    this.setIds([]);
    this.setMeta(null);
    this.setLinks(null);
  },
  beforeRouteEnter(to, from, next) {
    const check = middleware('account-permission:read clients');

    check(to, from, async (args) => {
      if (typeof args !== 'undefined') {
        return next(...args);
      }

      const { accountId } = to.params;
      const { query: params } = to;

      try {
        await store.dispatch('ui/accountClients/load', { accountId, params });
        return next();
      } catch (error) {
        return next(false);
      }
    });
  },
  methods: {
    ...mapActions('ui/accountClients', {
      loadAccountClients: 'load',
    }),
    ...mapMutations('ui/accountClients', ['setIds', 'setMeta', 'setLinks']),
    async fetchData() {
      const { accountId } = this;
      const { query: params } = this.$route;
      this.status = STATUS_PENDING;
      try {
        await this.loadAccountClients({ accountId, params });
        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;
      }
    },
    viewClient(client) {
      if (!client.can.view) return;

      this.$router.push({
        name: 'accounts.accountId.clients.clientUuid',
        params: {
          accountId: this.accountId,
          clientUuid: client.uuid,
        },
      });
    },
    async handleExport() {
      this.exporting = true;

      try {
        const response = await accountAccountClientsClient.export(this.accountId, {
          responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        const filename = `${snakeCase(this.account.name)}_clients.xlsx`;

        link.href = url;
        link.setAttribute('download', filename);
        link.click();

        window.URL.revokeObjectURL(url);
      } catch (error) {
        /** handle error */
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
